import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Navigate, Link } from 'react-router-dom';
import { z } from 'zod';
import QRCode from 'qrcode';
import { Car, Loader2, ShieldCheck } from 'lucide-react';
import { codigoMfaSchema, loginSchema, type LoginInput, type RespostaLogin } from '@erp/shared';
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from '@erp/ui';
import { api, ApiError } from '../lib/api';
import { useSessao } from '../lib/sessao';

type Etapa =
  | { tipo: 'senha' }
  | { tipo: 'mfa'; token: string }
  | { tipo: 'cadastrar_mfa'; token: string; otpauthUrl: string };

export function Entrar() {
  const { usuario, entrou } = useSessao();
  const [etapa, setEtapa] = useState<Etapa>({ tipo: 'senha' });
  const [erro, setErro] = useState<string | null>(null);

  if (usuario) return <Navigate to="/" replace />;

  const tratar = (r: RespostaLogin) => {
    if (r.etapa === 'concluido') entrou(r.accessToken, r.usuario);
    else if (r.etapa === 'mfa') setEtapa({ tipo: 'mfa', token: r.tokenTemporario });
    else setEtapa({ tipo: 'cadastrar_mfa', token: r.tokenTemporario, otpauthUrl: r.otpauthUrl });
  };
  const falhou = (e: unknown) =>
    setErro(e instanceof ApiError ? e.message : 'Sem conexão. Tente de novo.');

  return (
    <main className="flex min-h-dvh items-center justify-center p-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="items-center text-center">
          <div className="mb-2 flex size-12 items-center justify-center rounded-xl bg-primary text-primary-foreground">
            {etapa.tipo === 'senha' ? <Car aria-hidden /> : <ShieldCheck aria-hidden />}
          </div>
          <CardTitle className="text-xl">
            {etapa.tipo === 'senha' ? 'Entrar no painel' : 'Verificação em duas etapas'}
          </CardTitle>
          <CardDescription>
            {etapa.tipo === 'senha' && 'Use o e-mail e a senha da sua loja.'}
            {etapa.tipo === 'mfa' && 'Digite o código de 6 dígitos do seu aplicativo autenticador.'}
            {etapa.tipo === 'cadastrar_mfa' &&
              'Para sua segurança, ative a verificação em duas etapas.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {erro && <Alert variant="erro">{erro}</Alert>}
          {etapa.tipo === 'senha' ? (
            <FormSenha
              onEnviar={(d) => {
                setErro(null);
                return api<RespostaLogin>('/auth/login', { method: 'POST', json: d }).then(
                  tratar,
                  falhou,
                );
              }}
            />
          ) : (
            <>
              {etapa.tipo === 'cadastrar_mfa' && <QrMfa url={etapa.otpauthUrl} />}
              <FormCodigo
                onEnviar={(codigo, confiarDispositivo) => {
                  setErro(null);
                  return api<RespostaLogin>('/auth/mfa', {
                    method: 'POST',
                    json: { tokenTemporario: etapa.token, codigo, confiarDispositivo },
                  }).then(tratar, falhou);
                }}
              />
              <Button variant="link" onClick={() => setEtapa({ tipo: 'senha' })}>
                Voltar
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </main>
  );
}

function FormSenha({ onEnviar }: { onEnviar: (d: LoginInput) => Promise<void> }) {
  const f = useForm<LoginInput>({ resolver: zodResolver(loginSchema) });
  return (
    <form className="flex flex-col gap-4" onSubmit={f.handleSubmit(onEnviar)} noValidate>
      <div className="flex flex-col gap-2">
        <Label htmlFor="email">E-mail</Label>
        <Input
          id="email"
          type="email"
          autoComplete="username"
          inputMode="email"
          aria-invalid={!!f.formState.errors.email}
          {...f.register('email')}
        />
        {f.formState.errors.email && (
          <p className="text-sm text-destructive">Informe um e-mail válido</p>
        )}
      </div>
      <div className="flex flex-col gap-2">
        <Label htmlFor="senha">Senha</Label>
        <Input
          id="senha"
          type="password"
          autoComplete="current-password"
          aria-invalid={!!f.formState.errors.senha}
          {...f.register('senha')}
        />
        {f.formState.errors.senha && <p className="text-sm text-destructive">Informe a senha</p>}
      </div>
      <Button type="submit" disabled={f.formState.isSubmitting}>
        {f.formState.isSubmitting && <Loader2 className="animate-spin" aria-hidden />}
        Entrar
      </Button>
      <Link
        to="/esqueci-senha"
        className="self-center text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
      >
        Esqueci minha senha
      </Link>
      <p className="mt-2 border-t pt-4 text-center text-sm text-muted-foreground">
        Ainda não usa o sistema?{' '}
        <Link to="/conheca" className="font-medium text-primary underline-offset-4 hover:underline">
          Conheça e teste grátis
        </Link>
      </p>
    </form>
  );
}

const codigoForm = z.object({ codigo: codigoMfaSchema, confiar: z.boolean() });

function FormCodigo({
  onEnviar,
}: {
  onEnviar: (codigo: string, confiar: boolean) => Promise<void>;
}) {
  const f = useForm<{ codigo: string; confiar: boolean }>({
    resolver: zodResolver(codigoForm),
    defaultValues: { confiar: false },
  });
  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={f.handleSubmit((d) => onEnviar(d.codigo, d.confiar))}
      noValidate
    >
      <div className="flex flex-col gap-2">
        <Label htmlFor="codigo">Código</Label>
        <Input
          id="codigo"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          className="text-center text-lg tracking-[0.5em]"
          aria-invalid={!!f.formState.errors.codigo}
          autoFocus
          {...f.register('codigo')}
        />
        {f.formState.errors.codigo && (
          <p className="text-sm text-destructive">Digite os 6 números</p>
        )}
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          className="size-4 accent-[var(--primary)]"
          {...f.register('confiar')}
        />
        Confiar neste aparelho por 30 dias
      </label>
      <Button type="submit" disabled={f.formState.isSubmitting}>
        Confirmar
      </Button>
    </form>
  );
}

function QrMfa({ url }: { url: string }) {
  const [img, setImg] = useState<string | null>(null);
  useEffect(() => {
    void QRCode.toDataURL(url, { margin: 1, width: 200 }).then(setImg);
  }, [url]);
  const segredo = new URL(url).searchParams.get('secret') ?? '';
  return (
    <div className="flex flex-col items-center gap-2 text-center text-sm">
      <p>1. Abra o Google Authenticator, Authy ou similar e escaneie:</p>
      {img ? (
        <img
          src={img}
          alt="QR code para o aplicativo autenticador"
          width={200}
          height={200}
          className="rounded-md bg-white p-2"
        />
      ) : (
        <div className="size-[200px]" />
      )}
      <p className="text-muted-foreground">
        Ou digite a chave: <code className="break-all font-mono text-foreground">{segredo}</code>
      </p>
      <p>2. Digite abaixo o código que aparecer.</p>
    </div>
  );
}
